import PropTypes from 'prop-types'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import VideoFlyer from './VideoFlyer'
import styles from './flier.scss'

import { flyerSelector, eventTokenSelector, isHostOrCohostSelector } from 'src/selectors'
import { updateFlyerZoom } from 'src/actions/flyer'
import Button from 'src/components/common/Button'
import LoadingIndicator from 'src/components/common/LoadingIndicator'
import { flyerDesignLayoutConfig } from 'src/assets/frontend-json/flyer-position'
import { VideoFlyerBackgrounds } from 'src/assets/frontend-json/video-flyer-backgrounds'
import { fetchEvent } from 'src/actions/event'
import { hasUserAccessToken } from 'src/services/access_tokens'
import { showLogin } from 'src/actions/login'
import PopConfirm from 'components/common/PopConfirm/PopConfirm'

const MAX_RETRY = 3
const RETRY_INTERVAL = 5000

function Flier({ event, onRetryFlyerUpload }) {
  const dispatch = useDispatch()
  const flyerZoom = useSelector(state => flyerSelector(state).flyerZoom)
  const eventToken = useSelector(eventTokenSelector)
  const isHostOrCohost = useSelector(isHostOrCohostSelector)

  const [showRetry, setShowRetry] = useState(false)
  const retryCountRef = useRef(0)
  const loadingRetryTimerRef = useRef(null)
  const hideTimerRef = useRef(null)
  const isStartedRef = useRef(false)
  const [showNotHostPermissionsModal, setShowNotHostPermissionsModal] = useState(false)

  const getDesignNumber = useCallback(() => {
    const flyer = event.pending_flyer || event.flyer
    const designNumber = flyer ? flyer.design_number : null
    return designNumber
  }, [event])

  useEffect(() => {
    hideTimerRef.current = setTimeout(() => {
      dispatch(updateFlyerZoom(false))
    }, 7000)
    return () => {
      clearTimeout(hideTimerRef.current)
      clearTimeout(loadingRetryTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (isLoading() && !showRetry && !loadingRetryTimerRef.current && !isStartedRef.current) {
      retryCountRef.current = 0
      setShowRetry(false)
      tryFetchEvent()
    }
    if (!isLoading() && showRetry) {
      clearTimeout(loadingRetryTimerRef.current)
      setShowRetry(false)
      isStartedRef.current = false
    }

    return () => {
      clearTimeout(loadingRetryTimerRef.current)
    }
  }, [])

  const isLoading = useCallback(() => {
    return event.flyer === null ||
      (event.pending_flyer && event.pending_flyer.state === 'pending') ||
      (event.flyer && event.flyer.state === 'pending') ||
      event.state === 'activation_pending_flier'
  }, [event])

  const tryFetchEvent = useCallback(() => {
    const flyer = event?.pending_flyer || event?.flyer
    const maxRetry = flyer?.type === 'VideoFlyer' ? MAX_RETRY + 2 : MAX_RETRY
    if (retryCountRef.current < maxRetry && isLoading()) {
      retryCountRef.current += 1
      if (eventToken) {
        dispatch(fetchEvent(eventToken))
      }
      loadingRetryTimerRef.current = setTimeout(tryFetchEvent, RETRY_INTERVAL)
    } else if (isLoading()) {
      setShowRetry(true)
    }
  }, [dispatch, eventToken, isLoading])

  const toggleZoom = useCallback(() => {
    dispatch(updateFlyerZoom(!flyerZoom))
  }, [dispatch, flyerZoom])

  const handleRetry = useCallback((e) => {
    e.stopPropagation()

    if (!hasUserAccessToken()) {
      dispatch(showLogin())
      return
    }

    if (!isHostOrCohost) {
      setShowNotHostPermissionsModal(true)
      return
    }

    const designNumbers = flyerDesignLayoutConfig
      .map(design => design.designIds)
      .reduce((a, b) => a.concat(b), [])

    const flyer = event.pending_flyer || event.flyer
    const designNumber = getDesignNumber()
    const actionDesignNumber = designNumbers.indexOf(Number(designNumber)) > -1 ? designNumber : null
    if (onRetryFlyerUpload) {
      onRetryFlyerUpload(
        flyer,
        actionDesignNumber
      )
    }
    setShowRetry(false)
  }, [event, getDesignNumber, onRetryFlyerUpload, isHostOrCohost])

  const renderLegacyFlyer = useCallback((flier) => (
    <img className={styles['flier']} src={flier.watermarked_url_2x} />
  ), [])

  const renderFlyer = useCallback(() => {
    const { flyer, flier } = event
    if (flyer.type === 'VideoFlyer') {
      return <VideoFlyer
        className={styles['flier']}
        flyer={flyer}
        cancelHideFlyerTimer={() => clearTimeout(hideTimerRef.current)}
        unZoomFlyer={() => dispatch(updateFlyerZoom(false))}
      />
    } else {
      return renderLegacyFlyer(flier || flyer)
    }
  }, [event, dispatch, renderLegacyFlyer])

  const renderLoading = useCallback(() => {
    const designNumber = String(getDesignNumber())
    const isVideoFlyer = designNumber && Object.keys(VideoFlyerBackgrounds).includes(designNumber.toString())
    const imageBgSlug = isVideoFlyer ? VideoFlyerBackgrounds[designNumber] : null

    return (
      (
        <div
          className={styles['loading-container']}
          style={{
            backgroundImage: imageBgSlug ? `url(/assets/Layouts/Backgrounds/${imageBgSlug}.jpg)` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className={styles['loading-content']}>
            <div className={styles['loading-indicator']} >
              <LoadingIndicator size='30px' />
              <p>Loading...</p>
            </div>
            {
              showRetry && (
                <div className={styles['retry-container']}>
                  <Button className={styles['retry-btn']} onClick={handleRetry}>
                    Retry
                  </Button>
                  <p>Looks like your flyer didn’t upload. Please try uploading it again or select a different one.</p>
                </div>
              )
            }
          </div>
        </div>
      )
    )
  }, [showRetry, isHostOrCohost])

  const containerClassName = flyerZoom ? 'flier-container-zoom' : 'flier-container'
  const loading = isLoading()

  return (
    <>

      <div className={styles[containerClassName]} id='rsvp' onClick={toggleZoom}>
        {
          loading ? (
            <div className={styles['flyer-empty-container']}>
              {
                renderLoading()
              }
            </div>
          ) : renderFlyer()
        }
      </div>

      <PopConfirm
        visible={showNotHostPermissionsModal}
        onClose={() => setShowNotHostPermissionsModal(false)}
        onConfirm={() => setShowNotHostPermissionsModal(false)}
        confirmText="OK"
        cancelText={null}
        type="normal"
      >
        <div className={styles['not-host-permissions-modal']}>
          You need Host or Cohost permissions to edit this event.
        </div>
      </PopConfirm>
    </>
  )
}

Flier.propTypes = {
  event: PropTypes.object,
  onRetryFlyerUpload: PropTypes.func
}

export default Flier
